import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { Field, getIn, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { InputSelect, FormikInput, FormikDatePicker, FormikSelect } from '../../../components';
import { indexEmployeesRequest, indexSelectEmployeesRequest } from '../../../requests/employees';
import { handleDateDiff, attachValuesFromPayroll } from '../helpers';
import { payrollSettlementRequest } from '../../../requests/payrollSettlements';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import SubmitButton from './SubmitButton';
import { indexTerminationReasonsRequest } from '../../../requests/terminationReasons';
import useTerminationReasonHook from '../hooks/useTerminationReasonHook';

const singleOptions = [
  { label: 'Si', value: 'true' },
  { label: 'No', value: 'false' }
];

function WorkerData({ companyID, settlementID }) {
  const { errors, touched, setFieldValue, values, submitForm, isSubmitting, setFieldTouched } = useFormikContext();
  const dispatch = useDispatch();
  const [employees, setEmployees] = useState([]);
  const isFirstRender = useRef(true);
  const [terminationReasons, setTerminationReasons] = useState([]);
  const { noticeMonthDisabled } = useTerminationReasonHook(values);
  const selectedTerminationReason = getIn(values, 'terminationReason');

  const handleSuccessTermination = ({ data }) => {
    setTerminationReasons(data.data);
  };

  const fetchTerminationReasons = useCallback(() => {
    indexTerminationReasonsRequest({
      dispatch,
      params: {
        paginate: false
      },
      successCallback: handleSuccessTermination,
      callback: () => {}
    });
  }, [dispatch]);

  const settlementEndDate = getIn(values, 'endDate');
  const settlementEmployee = getIn(values, 'employee[id]');

  const fetchEmployees = (inputValue, callback) => {
    indexSelectEmployeesRequest({
      dispatch,
      params: {
        active: true,
        filter_name: inputValue,
        active_contracts: true,
        paginate: false,
        sort_column: 'name',
        sort_direction: 'asc',
        filter_identification_type: 'rut'
      },
      successCallback: response => {
        if (response.data.data.length > employees.length) {
          setEmployees(response.data.data);
        }
        callback(response.data.data);
      }
    });
  };

  const fetchPayrollData = endDate => {
    if (!endDate || !values.employee.id) return;
    const dateToSearch = endDate;
    payrollSettlementRequest({
      dispatch,
      params: {
        filter_by_company: companyID,
        filter_employees: [values.employee.id],
        month_date: dateToSearch.format('YYYY/MM/DD'),
        year_date: dateToSearch.format('YYYY/MM/DD'),
        settlement_data: true
      },
      successCallback: ({ data }) => {
        attachValuesFromPayroll(setFieldValue, camelCaseEmptyStringRecursive(data), values);
      }
    });
  };

  const handleSuccessEmployee = ({ data }) => {
    if (data.data.length === 0) return;
    const employee = data.data[0];
    const employeeForm = {
      ...employee,
      costCenter: employee.cost_center || '',
      jobManagement: employee.job_management || '',
      subsidiary: employee.subsidiary || '',
      workedTime: '',
      contractType: employee.parsed_contract_type,
      startDate: moment(employee.start_date, 'YYYY-MM-DD').format('DD/MM/YYYY')
    };
    setFieldValue('employee', camelCaseRecursive(employeeForm));
    setFieldValue('nonWorkingDays', 0);
    const hasTerminationDate = employee.termination_date;
    if (employee.termination_reason) {
      setFieldValue('terminationReasonObject', employee.termination_reason);
    }
    if (hasTerminationDate) {
      setFieldValue('endDate', '');
      const date = moment(employee.termination_date, 'YYYY-MM-DD');
      setFieldValue('endDate', date.format('DD/MM/YYYY'));
    }
    if (!hasTerminationDate) {
      setFieldValue('endDate', '');
    }
  };

  const fetchEmployeeData = employeeID => {
    indexEmployeesRequest({
      params: {
        include_ids: employeeID,
        settlement: true
      },
      dispatch,
      successCallback: handleSuccessEmployee,
      failureCallback: () => {}
    });
  };

  const initialDefaultValues = (array, attributeId) => {
    const data = array.find(obj => obj.value === attributeId);
    if (data === undefined) return false;
    return data;
  };

  const handleChangeEndDate = () => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setFieldValue('employee[workedTime]', '');
    if (!settlementEndDate || !settlementEmployee) return;
    const date = moment(settlementEndDate, 'DD/MM/YYYY');
    if (moment.isMoment(date)) {
      setFieldValue('employee[workedTime]', handleDateDiff(values.employee.startDate, date.format('DD/MM/YYYY')));
      fetchPayrollData(date);
    }
  };

  useEffect(handleChangeEndDate, [settlementEndDate]);
  useEffect(fetchTerminationReasons, []);
  useEffect(() => {
    if (terminationReasons.length === 0) return;
    if (!selectedTerminationReason) return;
    const selectedReason = terminationReasons.find(reason => reason.value === selectedTerminationReason);
    if (!selectedReason) return;
    if (selectedReason !== values.terminationReasonObject) {
      setFieldValue('terminationReasonObject', selectedReason || {});
    }
  }, [selectedTerminationReason, setFieldValue, terminationReasons, values.terminationReasonObject]);

  return (
    <>
      <Col>
        <Row className="align-items-center">
          <Col xs="auto">
            <span className="col-form-label">
              Simulación <abbr className="text-danger ml-1">*</abbr>
            </span>
          </Col>
          <Col xs={12} md={2} lg={2} xl={2}>
            <Field name="simulation">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  placeholder="Si"
                  options={singleOptions}
                  value={values.simulation}
                  defaultValue={values?.simulation}
                  onChange={data => setFieldValue(field.name, data ? data.value : '')}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
        <Col className="top-header-green br-5">
          <div className="card-header-title">DATOS DEL TRABAJADOR</div>
        </Col>
        <Card.Body className="div-content">
          <>
            <Row>
              <Col xl={3} md={3} lg={3} xs={12}>
                <Field name="employee[id]">
                  {({ field }) => (
                    <InputSelect
                      {...field}
                      abbr
                      label="Trabajador"
                      placeholder="Seleccionar Trabajador"
                      request={fetchEmployees}
                      value={initialDefaultValues(employees, values.employee?.id)}
                      error={getIn(errors, field.name)}
                      onChange={data => {
                        fetchEmployeeData(data.value);
                        setFieldValue(field.name, data ? data.value : '');
                      }}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} md={3} lg={3} xs={12}>
                <Field name="employee[nationalIdentification]">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Rut Trabajador"
                      error={getIn(errors, field.name)}
                      value={values?.employee?.nationalIdentification}
                      touched={getIn(touched, field.name)}
                      readOnly
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} md={3} lg={3} xs={12}>
                <Field name="employee[jobTitle]">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Cargo"
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      readOnly
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} md={3} lg={3} xs={12}>
                <Field name="employee[contractType]">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Tipo de Contrato"
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      readOnly
                    />
                  )}
                </Field>
              </Col>
            </Row>
            <Row>
              <Col xl={3} md={3} lg={3} xs={12} className="settlement_settings__date-picker">
                <Field name="employee[startDate]">
                  {({ field }) => (
                    <FormikDatePicker
                      {...field}
                      isOutsideRange={() => false}
                      label="Fecha de Ingreso"
                      placeholder="dd/mm/aaaa"
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      customCloseIcon={<></>}
                      onFocusChange={() => {}}
                      disabled
                      readOnly
                    />
                  )}
                </Field>
              </Col>
            </Row>
          </>
        </Card.Body>
        <Row />
        <Col className="top-header-green br-5">
          <div className="card-header-title">DATOS GENERALES DEL FINIQUITO</div>
        </Col>
        <Card.Body className="div-content">
          <>
            <Row>
              <Col xl={6} xs={12} md={6} lg={6}>
                <Field name="terminationReason">
                  {({ field }) => (
                    <FormikSelect
                      {...field}
                      abbr
                      label="Causal de Término Contrato"
                      placeholder="Causal de Término Contrato"
                      options={terminationReasons}
                      value={values?.terminationReason}
                      defaultValue={values?.terminationReason}
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} xs={12} md={3} lg={3}>
                <Field name="noticeMonth">
                  {({ field }) => (
                    <FormikSelect
                      {...field}
                      abbr
                      label="Mes de Aviso"
                      placeholder="Si"
                      options={singleOptions}
                      value={values.noticeMonth}
                      defaultValue={values?.noticeMonth}
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      setFieldTouched={() => setFieldTouched(field.name)}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      isDisabled={noticeMonthDisabled}
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} xs={12} md={3} lg={3}>
                <Field name="variableIncome">
                  {({ field }) => (
                    <FormikSelect
                      {...field}
                      abbr
                      label="Renta Variable"
                      placeholder="Si"
                      options={singleOptions}
                      value={values.variableIncome}
                      defaultValue={values?.variableIncome}
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      setFieldTouched={() => setFieldTouched(field.name)}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
            </Row>
            <Row>
              <Col xl={3} xs={12} md={3} lg={3}>
                <Field name="issueDate">
                  {({ field }) => (
                    <FormikDatePicker
                      {...field}
                      isOutsideRange={() => false}
                      value={values.issueDate}
                      label="Fecha de Emisión"
                      placeholder="dd/mm/aaaa"
                      onDateChange={date => {
                        setFieldValue(field.name, moment.isMoment(date) ? date.format('DD/MM/YYYY') : '');
                      }}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} md={3} lg={3} xs={12}>
                <Field name="endDate">
                  {({ field }) => (
                    <FormikDatePicker
                      {...field}
                      abbr
                      isOutsideRange={day => {
                        day.isBefore(moment(values.employee.startDate, 'DD-MM-YYYY'));
                      }}
                      label="Fecha de Término"
                      placeholder="dd/mm/aaaa"
                      onDateChange={date => {
                        setFieldValue('employee[workedTime]', '');
                        setFieldValue(field.name, moment.isMoment(date) ? date.format('DD/MM/YYYY') : '');
                      }}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} xs={12} md={3} lg={3}>
                <Field name="paymentDate">
                  {({ field }) => (
                    <FormikDatePicker
                      {...field}
                      abbr
                      isOutsideRange={() => false}
                      value={values.paymentDate}
                      label="Fecha de Pago"
                      placeholder="dd/mm/aaaa"
                      onDateChange={date => {
                        setFieldValue(field.name, moment.isMoment(date) ? date.format('DD/MM/YYYY') : '');
                      }}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} md={3} lg={3} xs={12}>
                <Field name="employee[workedTime]">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Tiempo Trabajado"
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      readOnly
                    />
                  )}
                </Field>
              </Col>
            </Row>
            <Row>
              <Col xl={12} xs={12} md={12} lg={12} style={{ paddingTop: 10 }}>
                <Field name="justificationTerminationReason">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Justificación de la Causal de Término de Contrato"
                      value={values.justificationTerminationReason}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      as="textarea"
                      maxLength={250}
                      rows={3}
                      helpText={`${values.justificationTerminationReason.length} / 250`}
                    />
                  )}
                </Field>
              </Col>
            </Row>
            <Row>
              <Col xl={3} xs={12} md={3} lg={3}>
                <Field name="ufValue">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Valor UF"
                      placeholder=""
                      value={values.parsedUfValue}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      readOnly
                    />
                  )}
                </Field>
              </Col>
              <Col xl={3} xs={12} md={3} lg={3}>
                <Field name="max90Uf">
                  {({ field }) => (
                    <FormikInput
                      {...field}
                      label="Tope 90 UF"
                      placeholder=""
                      value={values.parsed90UfValue}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                      readOnly
                    />
                  )}
                </Field>
              </Col>
            </Row>
          </>
        </Card.Body>
        <SubmitButton
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          settlementID={settlementID}
          showCalculateButton
        />
      </Col>
    </>
  );
}

export default WorkerData;
